import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Compass, MapPinned, SlidersHorizontal, Footprints } from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";
import { IMAGES } from "../lib/constants";
import { Reveal, PageFade } from "../components/Motion";
import { SectionHead, Disclaimer } from "../components/Common";
import { PropertyCard } from "../components/PropertyCard";

const TRUST = [
  [MapPinned, "Local Knowledge", "Focused exclusively on Oregon markets."],
  [Footprints, "Hands-On Acquisition", "We don't simply send listings. We investigate opportunities and do the local legwork."],
  [SlidersHorizontal, "Investor Specific", "We search based on each investor's individual criteria, strategy, budget, and goals."],
];

const STEPS = [
  ["01", "Tell Us What You Want", "Investors submit their acquisition criteria, including property type, location, budget, investment strategy, desired returns, condition, and other requirements."],
  ["02", "We Search", "Sido Holdings searches Oregon markets for opportunities that may fit the investor's criteria."],
  ["03", "We Investigate", "We research the property, communicate with relevant parties, visit properties when appropriate, gather information, and help investors evaluate the opportunity."],
  ["04", "Move Forward", "When an opportunity fits, Sido Holdings helps coordinate the next steps toward acquisition, including negotiations and transaction coordination within the scope of applicable licensing and legal requirements."],
];

const FOCUS = ["Commercial real estate", "Foreclosures", "Distressed properties", "Off-market opportunities", "Value-add opportunities", "Investor-specific acquisitions"];

function Hero() {
  return (
    <section className="relative min-h-[100svh] bg-navy text-paper overflow-hidden grain" data-testid="home-hero">
      <img src={IMAGES.hero} alt="Portland, Oregon commercial skyline" className="absolute inset-0 h-full w-full object-cover scale-105" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-navy/40" />
      <div className="container-x relative flex min-h-[100svh] flex-col justify-end pb-16 sm:pb-24 pt-32">
        <Reveal><p className="eyebrow">Oregon Commercial Real Estate, Sourced for You</p></Reveal>
        <Reveal delay={0.1}>
          <h1 className="mt-6 max-w-5xl text-[44px] sm:text-6xl lg:text-[88px] font-medium leading-[0.98] tracking-tight" data-testid="hero-headline">
            You Set the Criteria. <span className="italic text-gold">We Find</span> the Opportunity.
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-2xl text-base md:text-lg text-paper/75 leading-relaxed">
            Sido Holdings helps investors find commercial real-estate opportunities across Oregon without spending their time searching, calling, analyzing, and chasing properties themselves.
          </p>
        </Reveal>
        <Reveal delay={0.3} className="mt-10 flex flex-col sm:flex-row gap-3">
          <Link to="/investors#criteria" className="btn-gold !px-8 !py-4" data-testid="hero-cta-criteria">Tell Us What You're Looking For <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/opportunities" className="btn-ghost !px-8 !py-4 text-paper hover:bg-paper hover:text-navy" data-testid="hero-cta-opportunities">View Opportunities</Link>
        </Reveal>
        <Reveal delay={0.4} className="mt-16 hidden md:grid grid-cols-3 max-w-3xl border-t border-paper/15 pt-6 text-sm text-paper/60">
          <span>Oregon only</span><span>Commercial & distressed</span><span>Investor-driven search</span>
        </Reveal>
      </div>
    </section>
  );
}

function Trust() {
  return (
    <section className="bg-paper py-20 sm:py-28" data-testid="trust-strip">
      <div className="container-x grid gap-14 lg:grid-cols-[1fr_2fr]">
        <Reveal><h2 className="text-3xl sm:text-4xl font-medium text-navy leading-tight">Oregon Focused.<br />Investor Driven.<br /><span className="italic text-gold">Hands-On.</span></h2></Reveal>
        <div className="grid gap-px bg-navy/10 sm:grid-cols-3">
          {TRUST.map(([Icon, t, d], i) => (
            <Reveal key={t} delay={i * 0.1} className="bg-paper p-6 sm:p-8">
              <Icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
              <h3 className="mt-6 text-2xl font-semibold text-navy">{t}</h3>
              <p className="mt-3 text-sm text-ink/70 leading-relaxed">{d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="bg-navy text-paper py-24 sm:py-32 relative grain" data-testid="how-it-works">
      <div className="container-x">
        <SectionHead light eyebrow="How It Works" title="Four steps from criteria to closing table." sub="Investors aren't submitting a form into a faceless database. You're connecting with a local real-estate professional who actively searches on your behalf." />
        <div className="mt-16 grid gap-px bg-paper/10 md:grid-cols-2 lg:grid-cols-4">
          {STEPS.map(([n, t, d], i) => (
            <Reveal key={n} delay={i * 0.08} className="bg-navy p-8 group hover:bg-navy-2 transition-colors duration-500">
              <span className="font-mono text-sm text-gold">{n} —</span>
              <h3 className="mt-8 text-3xl font-medium">{t}</h3>
              <p className="mt-4 text-sm text-paper/65 leading-relaxed">{d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Featured() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/properties", { params: { featured: true } }).then((r) => setItems(r.data.slice(0, 3))).catch(() => {}); }, []);
  return (
    <section className="bg-paper py-24 sm:py-32" data-testid="featured-opportunities">
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <SectionHead eyebrow="Current Opportunities" title="A look at what we're sourcing." />
          <Link to="/opportunities" className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-gold" data-testid="featured-view-all">View all opportunities <ArrowUpRight className="h-4 w-4" /></Link>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {items.map((p, i) => <Reveal key={p.id} delay={i * 0.08}><PropertyCard p={p} /></Reveal>)}
        </div>
        <div className="mt-10"><Disclaimer testid="home-disclaimer" /></div>
      </div>
    </section>
  );
}

function Mission() {
  return (
    <section className="bg-white py-24 sm:py-32 border-y border-navy/10" data-testid="mission-section">
      <div className="container-x grid gap-14 lg:grid-cols-[1.2fr_1fr] items-center">
        <Reveal>
          <p className="eyebrow">Our Mission</p>
          <blockquote className="mt-6 font-serif text-2xl sm:text-3xl lg:text-[38px] leading-[1.25] text-navy">
            “Our mission is to make commercial real estate investing simpler, more efficient, and more accessible by doing the legwork investors don't have time to do. Sido Holdings identifies opportunities across Oregon, evaluates them against each investor's goals, and helps move qualified opportunities from discovery to transaction with professionalism, transparency, and local expertise.”
          </blockquote>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative">
            <img src={IMAGES.night} alt="Portland skyline at night" className="aspect-[4/5] w-full object-cover" loading="lazy" />
            <div className="absolute -bottom-6 -left-6 bg-navy text-paper p-6 max-w-[260px] hidden sm:block">
              <Compass className="h-5 w-5 text-gold" />
              <p className="mt-3 text-sm leading-relaxed">We focus on:</p>
              <ul className="mt-2 space-y-1 text-[13px] text-paper/70">{FOCUS.map((f) => <li key={f}>— {f}</li>)}</ul>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Ctas() {
  return (
    <section className="grid lg:grid-cols-2" data-testid="home-ctas">
      <div className="relative bg-navy text-paper px-6 sm:px-12 lg:px-16 py-24 overflow-hidden grain" data-testid="investor-cta">
        <img src={IMAGES.glass} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" loading="lazy" />
        <div className="relative max-w-xl">
          <p className="eyebrow">For Investors</p>
          <h2 className="mt-5 text-4xl sm:text-5xl font-medium leading-tight">Stop Searching. Start Acquiring.</h2>
          <p className="mt-6 text-paper/75 leading-relaxed">You shouldn't have to spend your evenings searching listings, calling owners, driving to properties, and chasing opportunities. Tell us exactly what you're looking for and let Sido Holdings handle the local search.</p>
          <Link to="/investors#criteria" className="btn-gold mt-10" data-testid="investor-cta-btn">Submit Your Investment Criteria <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
      <div className="bg-[#EDECE6] px-6 sm:px-12 lg:px-16 py-24" data-testid="seller-cta">
        <div className="max-w-xl">
          <p className="eyebrow">For Owners & Sellers</p>
          <h2 className="mt-5 text-4xl sm:text-5xl font-medium leading-tight text-navy">Have a Commercial Property to Sell?</h2>
          <p className="mt-6 text-ink/70 leading-relaxed">Sido Holdings works with property owners, sellers, and other sources to identify commercial real-estate opportunities for qualified investors. If you're considering selling a commercial property, tell us about it.</p>
          <Link to="/sell" className="btn-primary mt-10" data-testid="seller-cta-btn">Submit a Property <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
    </section>
  );
}

function PmTeaser() {
  return (
    <section className="bg-paper py-20" data-testid="pm-teaser">
      <div className="container-x flex flex-col md:flex-row md:items-center justify-between gap-8 border border-navy/10 bg-white p-8 sm:p-12">
        <div className="max-w-2xl">
          <p className="eyebrow">Property Management</p>
          <h3 className="mt-3 text-3xl font-medium text-navy">Already own commercial real estate?</h3>
          <p className="mt-3 text-ink/70">Sido Holdings can also help simplify the ownership experience through property management services, subject to applicable licensing and service availability.</p>
        </div>
        <Link to="/property-management" className="btn-ghost text-navy hover:bg-navy hover:text-paper shrink-0" data-testid="pm-teaser-link">Learn More</Link>
      </div>
    </section>
  );
}

export default function Home() {
  useSeo(null, "Oregon commercial real estate acquisition partner. Tell Sido Holdings your investment criteria and we source, evaluate and help execute commercial, off-market, foreclosure and distressed opportunities across Oregon.");
  return (
    <PageFade>
      <Hero />
      <Trust />
      <HowItWorks />
      <Featured />
      <Mission />
      <Ctas />
      <PmTeaser />
    </PageFade>
  );
}
